// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useState } from 'react';

import { type TaskTypeGroup, type TaskType, useEvolvePaginatedGet } from '@evolvemep/api-ts';

import { useGetAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { TaskTypeList } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/TaskTypes/TaskTypeList';

import { DefinedTaskTypeGroupSection } from './DefinedTaskTypeGroupSection';
import { useTaskTypes } from './useTaskTypes';

export const DefinedTaskGroup = ({
  taskTypeGroup,
  expanded,
  hideGroupSelect,
  hideDelete,
  isWorkCellMapping,
}: {
  taskTypeGroup: TaskTypeGroup;
  expanded: boolean;
  hideGroupSelect: boolean;
  hideDelete?: boolean;
  isWorkCellMapping?: boolean;
}) => {
  const [disableGroupSelect, setDisableGroupSelect] = useState(false);

  const {
    selectedTasks,
    onGroupSelect,
    selectedGroups,
    setSelectedGroups,
    onTaskSelect,
    searchText,
    shopConfiguration,
  } = useTaskTypes();

  const { fetchPage, entireCount } = useEvolvePaginatedGet('shop/taskType');

  const [definedGroupTasks, loadingGroupTasks, { setWait, setConfig }] = useGetAllDataFromFetcher(fetchPage, {}, true);

  useEffect(() => {
    // if group is expanded and all task types in the group have already been mapped to a work cell,
    // disable selection of the overall group.
    if (definedGroupTasks.length && !definedGroupTasks.some((t) => !t.isMappedToWorkCell)) {
      setDisableGroupSelect(true);
      setSelectedGroups((prev) => prev.filter((g) => g.taskTypeGroupId !== taskTypeGroup.taskTypeGroupId));
    } else {
      setDisableGroupSelect(false);
    }
  }, [definedGroupTasks, setSelectedGroups, taskTypeGroup.taskTypeGroupId]);

  useEffect(() => {
    setConfig({
      params: {
        taskTypeGroupId: taskTypeGroup.taskTypeGroupId,
        taskTypeName: searchText,
        shopConfigurationId: shopConfiguration?.shopConfigurationId,
      },
    });
  }, [searchText, setConfig, shopConfiguration?.shopConfigurationId, taskTypeGroup.taskTypeGroupId]);

  useEffect(() => {
    setWait(!expanded);
  }, [expanded, setWait]);

  const selectedTasksFromGroup = selectedTasks.filter((st) => st.taskTypeGroupId === taskTypeGroup.taskTypeGroupId);
  const unmappedTasksFromGroup = definedGroupTasks.filter((t) => !t.isMappedToWorkCell);

  const onSelectAll = () => {
    if (
      definedGroupTasks.length > 0 &&
      !selectedGroups.some((g) => g.taskTypeGroupId === taskTypeGroup.taskTypeGroupId)
    ) {
      if (unmappedTasksFromGroup.length === 0 && selectedTasksFromGroup.length === 0) {
        definedGroupTasks.forEach((t) => onTaskSelect(t));
      } else if (selectedTasksFromGroup.length === unmappedTasksFromGroup.length) {
        unmappedTasksFromGroup.forEach((t) => onTaskSelect(t));
      } else {
        definedGroupTasks
          .filter(
            (t) =>
              !selectedTasks.some((st) => st.taskTypeId === t.taskTypeId) &&
              unmappedTasksFromGroup.some((ut) => ut.taskTypeId === t.taskTypeId),
          )
          .forEach((t) => onTaskSelect(t));
      }
    } else {
      onGroupSelect(taskTypeGroup);
    }
  };

  const checked =
    selectedGroups.some((g) => g.taskTypeGroupId === taskTypeGroup.taskTypeGroupId) ||
    (definedGroupTasks.length > 0 &&
      selectedTasksFromGroup.length > 0 &&
      selectedTasksFromGroup.length === unmappedTasksFromGroup.length);

  const checkIfShouldSelectGroup = useCallback(
    (task: TaskType) => {
      const selectedGroupTasks = selectedTasks.filter((st) => st.taskTypeGroupId === task.taskTypeGroupId);
      if (hideGroupSelect) {
        onTaskSelect(task);
      } else {
        if (
          !selectedTasks.some((st) => st.taskTypeId === task.taskTypeId) &&
          selectedGroupTasks.length + 1 === definedGroupTasks.length
        ) {
          onGroupSelect(taskTypeGroup);
          selectedGroupTasks.forEach((t) => onTaskSelect(t));
        } else if (selectedGroups.some((g) => g.taskTypeGroupId === taskTypeGroup.taskTypeGroupId)) {
          onGroupSelect(taskTypeGroup);
          definedGroupTasks.filter((t) => t.taskTypeId !== task.taskTypeId).forEach((t) => onTaskSelect(t));
        } else {
          onTaskSelect(task);
        }
      }
    },
    [definedGroupTasks, hideGroupSelect, onGroupSelect, onTaskSelect, selectedGroups, selectedTasks, taskTypeGroup],
  );

  return (
    <DefinedTaskTypeGroupSection
      taskTypeGroup={taskTypeGroup}
      checked={checked}
      onSelectAll={onSelectAll}
      disableGroupSelect={disableGroupSelect}
      hideGroupSelect={hideGroupSelect}
    >
      <TaskTypeList
        loading={loadingGroupTasks}
        onTaskSelect={checkIfShouldSelectGroup}
        groupTasks={definedGroupTasks}
        entireCount={entireCount}
        hideDelete={hideDelete}
        isWorkCellMapping={isWorkCellMapping}
      />
    </DefinedTaskTypeGroupSection>
  );
};
